import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarScrollWrapper,
  SidebarSpace,
} from '@backstage/core-components';
import { useEffect, useState } from 'react';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { NavContentBlueprint } from '@backstage/plugin-app-react';
import { SidebarLogo } from './SidebarLogo';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import HomeIcon from '@material-ui/icons/Home';
import TimelineIcon from '@material-ui/icons/Timeline';
import QuestionAnswerIcon from '@material-ui/icons/QuestionAnswer';
import InsightsIcon from '@material-ui/icons/TrendingUp';
import EventNoteIcon from '@material-ui/icons/EventNote';
import { SidebarSearchModal } from '@backstage/plugin-search';
import { UserSettingsSignInAvatar } from '@backstage/plugin-user-settings';
import { NotificationsSidebarItem } from '@backstage/plugin-notifications';
import { usePermission } from '@backstage/plugin-permission-react';
import { catalogLocationCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { taskCreatePermission } from '@backstage/plugin-scaffolder-common/alpha';

// Parceiros externos pertencem a um time "Parceiro - <nome>" (grupo parceiro-*).
// Para eles os painéis internos (Value Stream, Desenvolvimento) não aparecem.
// Só esconde o item de menu; quem de fato barra o acesso a dados é o RBAC.
function useIsPartner(): boolean {
  const identityApi = useApi(identityApiRef);
  const [isPartner, setIsPartner] = useState(false);
  useEffect(() => {
    let active = true;
    identityApi
      .getBackstageIdentity()
      .then(id => {
        if (active) {
          setIsPartner(id.ownershipEntityRefs.some(ref => /^group:[^/]+\/parceiro-/i.test(ref)));
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [identityApi]);
  return isPartner;
}

export const SidebarContent = NavContentBlueprint.make({
  params: {
    component: ({ navItems }) => {
      // ── Checagem de permissões ─────────────────────────────────────────────
      // Enquanto carrega (loading=true) o item aparece (abordagem otimista).
      // Após a resolução, itens negados são ocultados sem flicker.

      const { loading: loadingScaffolder, allowed: canUseScaffolder } =
        usePermission({ permission: taskCreatePermission });

      const { loading: loadingImport, allowed: canImportCatalog } =
        usePermission({ permission: catalogLocationCreatePermission });

      const isPartner = useIsPartner();

      // ── Preparação da navegação ────────────────────────────────────────────
      const nav = navItems.withComponent(item => (
        <SidebarItem icon={() => item.icon} to={item.href} text={item.title} />
      ));

      // Itens extraídos do pool antes do JSX para que nunca apareçam no rest().
      // A renderização condicional é feita abaixo.
      nav.take('page:search'); // substituído pelo search modal
      nav.take('page:catalog-unprocessed-entities'); // gestão migrou para o dashboard
      const onboardingNav = nav.take('page:app/onboarding');
      // Squads saiu do menu: a página segue existindo e é alcançada por URL,
      // mas o que o time acompanha agora é o painel da value stream.
      nav.take('page:app/squads');
      // Só remove do pool: o item é renderizado explicitamente abaixo, como o
      // Q&A. Páginas de pluginId 'app' com nome próprio não estão chegando no
      // pool de navItems nesta versão — o take devolve undefined e o item
      // simplesmente não aparece. Mantido para evitar duplicata caso passem a vir.
      nav.take('page:app/value-stream');
      // Mudanças (GMUD) é renderizado explicitamente abaixo, na posição fixa do
      // menu; o take evita duplicata caso o plugin passe a registrar NavItem.
      nav.take('page:change-management');
      const scaffolderNav = nav.take('page:scaffolder');
      const catalogImportNav = nav.take('page:catalog-import');

      return (
        <Sidebar>
          <SidebarLogo />
          <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
            <SidebarSearchModal />
          </SidebarGroup>
          <SidebarDivider />
          <SidebarGroup label="Menu" icon={<MenuIcon />}>
            <SidebarItem icon={HomeIcon} to="/" text="Home" />
            {onboardingNav}
            {!isPartner && (
              <SidebarItem icon={TimelineIcon} to="/value-stream" text="Value Stream" />
            )}
            {nav.take('page:catalog')}
            {(loadingScaffolder || canUseScaffolder) && scaffolderNav}
            {(loadingImport || canImportCatalog) && catalogImportNav}
            <SidebarItem icon={EventNoteIcon} to="/change-calendar" text="Mudanças" />
            <SidebarItem icon={QuestionAnswerIcon} to="/qeta" text="Q&A" />
            {!isPartner && (
              <SidebarItem icon={InsightsIcon} to="/desenvolvimento" text="Desenvolvimento" />
            )}
            <SidebarDivider />
            <SidebarScrollWrapper>
              {nav.rest({ sortBy: 'title' })}
            </SidebarScrollWrapper>
          </SidebarGroup>
          <SidebarSpace />
          <SidebarDivider />
          <NotificationsSidebarItem />
          <SidebarDivider />
          <SidebarGroup
            label="Settings"
            icon={<UserSettingsSignInAvatar />}
            to="/settings"
          >
            {nav.take('page:app-visualizer')}
            {nav.take('page:user-settings')}
          </SidebarGroup>
        </Sidebar>
      );
    },
  },
});
